import { useCallback } from 'react';
import { Route, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getDisabledFeaturesList } from 'store/selectors/ui.selector';
import { getAuthState } from 'store/selectors/auth.selector';
import { getFetchingLendersFlag, getLendersList } from 'store/selectors/lenders.selector';
import { ErrorPage } from 'pages/ErrorPage/ErrorPage';
import { IMenuLink } from 'modules/core/components/SideBar/sideBarMenuLinks';

import { RouterItem } from '../RouterItem';

interface IUseDisabledFeatureRedirectParams {
  featureName: string;
  routePath: string;
  lenderFilterFn: (lender: IMenuLink) => boolean;
}

export const useDisabledFeatureRedirect = () => {
  const disabledFeaturesList = useSelector(getDisabledFeaturesList);
  const { isAuth } = useSelector(getAuthState);
  const isFetchingLendersList = useSelector(getFetchingLendersFlag);
  const lenders = useSelector(getLendersList);

  const shouldFeatureRedirect = useCallback(
    (featureName: string) =>
      disabledFeaturesList.includes(featureName) && isAuth && !isFetchingLendersList && lenders.length > 0,
    [disabledFeaturesList, isAuth, isFetchingLendersList, lenders.length],
  );

  const createFeatureRedirect = useCallback(
    (routePath: string, lenderFilterFn: (lender: IMenuLink) => boolean) => {
      const lender = lenders.find(lenderFilterFn);
      const redirectPath = lender?.link || lender?.path;

      if (redirectPath && typeof redirectPath === 'string' && redirectPath.trim()) {
        return <Redirect to={redirectPath} />;
      }

      return <RouterItem path={routePath} component={ErrorPage} isPrivate />;
    },
    [lenders],
  );

  const createRedirectRoute = useCallback(
    ({ featureName, routePath, lenderFilterFn }: IUseDisabledFeatureRedirectParams) => {
      const renderRedirect = () => createFeatureRedirect(routePath, lenderFilterFn);

      const renderRoute = () => {
        if (!shouldFeatureRedirect(featureName)) {
          return null;
        }

        return <Route exact path={routePath} render={renderRedirect} />;
      };

      return renderRoute;
    },
    [shouldFeatureRedirect, createFeatureRedirect],
  );

  return { createRedirectRoute };
};
